import { useEffect, useState, type FormEvent } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  adminClient,
  BROADCAST_STATUS_LABELS,
  type BroadcastMessage,
  type BroadcastPayload,
} from '../../services/adminClient'
import { Modal } from '../../components/Modal'
import { ConfirmModal } from '../../components/ConfirmModal'

type Mode = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; item: BroadcastMessage }

export function BroadcastPage() {
  const [items, setItems] = useState<BroadcastMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [confirmDelete, setConfirmDelete] = useState<BroadcastMessage | null>(null)
  const [deleting, setDeleting] = useState(false)

  function reload() {
    setLoading(true)
    adminClient.broadcast
      .list()
      .then((res) => setItems(res.items))
      .catch((err) =>
        setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [])

  async function executeDelete() {
    if (!confirmDelete) return
    setDeleting(true)
    try {
      await adminClient.broadcast.remove(confirmDelete.id)
      setItems((prev) => prev.filter((i) => i.id !== confirmDelete.id))
      setConfirmDelete(null)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo eliminar.')
      setConfirmDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Mensajes Broadcast</h1>
          <span className="muted">{items.length} mensaje(s)</span>
        </div>
        <div className="list-toolbar__actions">
          <button type="button" className="primary-btn" onClick={() => setMode({ kind: 'create' })}>
            + Nuevo mensaje
          </button>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Mensaje</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Estado</th>
              <th aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} className="data-table__empty">No hay mensajes.</td></tr>
            )}
            {items.map((m) => (
              <tr key={m.id} className="data-table__row">
                <td>{m.message}</td>
                <td className="mono">{m.start_date}</td>
                <td className="mono">{m.end_date}</td>
                <td>
                  <span className={`badge badge--${m.status === 'active' ? 'pdf_generated' : 'draft'}`}>
                    {BROADCAST_STATUS_LABELS[m.status]}
                  </span>
                </td>
                <td className="data-table__actions">
                  <button type="button" className="ghost-btn small" onClick={() => setMode({ kind: 'edit', item: m })}>
                    Editar
                  </button>
                  <button type="button" className="danger-btn small" onClick={() => setConfirmDelete(m)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <p className="muted center">Cargando…</p>}
      </div>

      <ConfirmModal
        open={confirmDelete !== null}
        title="Eliminar mensaje"
        message="¿Eliminar este mensaje? Esta acción es irreversible."
        confirmLabel="Eliminar"
        onConfirm={executeDelete}
        onCancel={() => setConfirmDelete(null)}
        loading={deleting}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <BroadcastFormModal
          key={mode.kind === 'edit' ? mode.item.id : 'new'}
          mode={mode.kind}
          item={mode.kind === 'edit' ? mode.item : null}
          onClose={() => setMode({ kind: 'list' })}
          onSaved={(saved) => {
            setItems((prev) => {
              const idx = prev.findIndex((p) => p.id === saved.id)
              if (idx === -1) return [saved, ...prev]
              const next = [...prev]
              next[idx] = saved
              return next
            })
            setMode({ kind: 'list' })
          }}
        />
      )}
    </div>
  )
}

function BroadcastFormModal({
  mode, item, onClose, onSaved,
}: {
  mode: 'create' | 'edit'
  item: BroadcastMessage | null
  onClose: () => void
  onSaved: (m: BroadcastMessage) => void
}) {
  const [message, setMessage] = useState(item?.message ?? '')
  const [startDate, setStartDate] = useState(item?.start_date ?? today())
  const [endDate, setEndDate] = useState(item?.end_date ?? addDays(today(), 30))
  const [active, setActive] = useState(item?.active ?? true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setFieldErrors({})
    setSubmitting(true)
    try {
      const payload: BroadcastPayload = {
        message: message.trim(),
        start_date: startDate,
        end_date: endDate,
        active,
      }
      const res = mode === 'create'
        ? await adminClient.broadcast.create(payload)
        : await adminClient.broadcast.update(item!.id, payload)
      onSaved(res.item)
    } catch (err) {
      if (err instanceof ApiClientError) {
        const fields = err.apiError.details?.fields
        if (fields && typeof fields === 'object') setFieldErrors(fields as Record<string, string>)
        setError(err.apiError.message)
      } else {
        setError('No se pudo guardar.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open
      onClose={() => !submitting && onClose()}
      title={mode === 'create' ? 'Nuevo mensaje broadcast' : 'Editar mensaje'}
      size="md"
      dismissible={!submitting}
      footer={
        <div className="modal__actions">
          <button type="button" className="ghost-btn" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" form="broadcast-form" className="primary-btn" disabled={submitting}>
            {submitting ? 'Guardando…' : mode === 'create' ? 'Crear' : 'Guardar cambios'}
          </button>
        </div>
      }
    >
      <form id="broadcast-form" className="form-grid" onSubmit={handleSubmit} noValidate>
        <label className="field">
          <span>Mensaje</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={2048}
            required
            autoFocus
            disabled={submitting}
          />
          {fieldErrors.message && <small className="field-error">{fieldErrors.message}</small>}
        </label>

        <div className="field-row">
          <label className="field">
            <span>Fecha de inicio</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required disabled={submitting} />
            {fieldErrors.start_date && <small className="field-error">{fieldErrors.start_date}</small>}
          </label>
          <label className="field">
            <span>Fecha fin</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required disabled={submitting} />
            {fieldErrors.end_date && <small className="field-error">{fieldErrors.end_date}</small>}
          </label>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            disabled={submitting}
          />
          <span>Mensaje activo (los usuarios lo verán si la fecha actual está en el rango)</span>
        </label>

        {error && Object.keys(fieldErrors).length === 0 && <p className="error">{error}</p>}
      </form>
    </Modal>
  )
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function addDays(date: string, days: number): string {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}
